import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LiveService } from "@/services/LiveService";
import { SpotRequestService } from "@/services/SpotRequestService";
import { Live, LiveFilters, Country, LiveDevice, Spot } from "@/common/model/Live";
import { ViewMode, SpotRequest, SpotRequestFilters, MergeSpotRequest, SpotAddRequest } from "@/common/model/SpotRequest";
import LiveSessionCard from "@/components/live/LiveSessionCard";
import LiveMap, { SpotSettings } from "@/components/live/LiveMap";
import LiveFiltersComponent from "@/components/live/LiveFilters";
import JumpDetailsModal from "@/components/live/JumpDetailsModal";
import {
  ViewModeSwitcher,
  RequestListPanel,
  ConfirmActionDialog,
  UserProfileDialog,
  ActionType,
} from "@/components/live/requests";
import AddSpotPanel from "@/components/live/requests/AddSpotPanel";
import { NearbySpot } from "@/services/SpotManagementService";
import { ArrowPathIcon, Cog6ToothIcon, MapPinIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

// Type for map flyTo coordinates
interface FlyToTarget {
  lat: number;
  lng: number;
  zoom?: number;
}

// Helper to detect device OS from device info
const getDeviceOs = (device: LiveDevice | null): "ios" | "android" | null => {
  if (!device) return null;
  const os = device.os?.toLowerCase() || "";
  const brand = device.brand?.toLowerCase() || "";
  const model = (device.model || "").toLowerCase();

  // Check for Apple/iOS
  if (brand.includes("apple") || os.includes("ios") || os.includes("iphone") ||
      model.includes("iphone") || model.includes("ipad")) {
    return "ios";
  }

  // Default to Android for other devices
  if (os || brand || model) {
    return "android";
  }

  return null;
};

// Helper to detect device type from device info
const getDeviceType = (device: LiveDevice | null): "phone" | "watch" | null => {
  if (!device) return null;
  const os = device.os?.toLowerCase() || "";
  const brand = device.brand?.toLowerCase() || "";

  // Check for watch
  if (os.includes("watch") || os.includes("wear") || brand.includes("garmin") || os.includes("garmin")) {
    return "watch";
  }

  // Default to phone for other devices
  if (os || brand || device.model) {
    return "phone";
  }

  return null;
};

const LivePage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [liveSessions, setLiveSessions] = useState<Live[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<Live[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [selectionSource, setSelectionSource] = useState<"map" | "list" | null>(null);
  const [hoveredSessionId, setHoveredSessionId] = useState<number | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [countries, setCountries] = useState<Country[]>([]);
  const [detailsSession, setDetailsSession] = useState<Live | null>(null);
  const [filters, setFilters] = useState<LiveFilters>({
    sort: "name",
    sortDir: "ASC",
  });

  // View mode state (riders vs requests) - initialized from URL param
  const initialViewMode = searchParams.get("view") as ViewMode;
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialViewMode === "requests" ? "requests" : "riders"
  );

  // Spot requests state
  const [spotRequests, setSpotRequests] = useState<SpotRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SpotRequest | null>(null);
  const [hoveredRequest, setHoveredRequest] = useState<SpotRequest | null>(null);
  const [requestFilters, setRequestFilters] = useState<SpotRequestFilters>({
    requestType: "all",
    sortBy: "newest",
    searchQuery: "",
  });
  const [filteredRequests, setFilteredRequests] = useState<SpotRequest[]>([]);

  // Add spot mode
  const [isAddingSpot, setIsAddingSpot] = useState(false);
  const [addSpotLocation, setAddSpotLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [addSpotNearbySpots, setAddSpotNearbySpots] = useState<NearbySpot[]>([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    request: SpotRequest | null;
    actionType: ActionType;
  }>({ isOpen: false, request: null, actionType: "approve" });
  const [isActionLoading, setIsActionLoading] = useState(false);

  // User profile dialog state
  const [userProfileDialog, setUserProfileDialog] = useState<{
    isOpen: boolean;
    userId: number;
    username: string;
  }>({ isOpen: false, userId: 0, username: "" });

  // Auto-refresh settings
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [countdown, setCountdown] = useState(30);
  const [showSettings, setShowSettings] = useState(false);

  // Spot settings
  const [spotSettings, setSpotSettings] = useState<SpotSettings>({
    showSpots: true,
    minZoomLevel: 10,
  });

  // Map flyTo target for spot selection
  const [flyToTarget, setFlyToTarget] = useState<FlyToTarget | null>(null);

  // Sidebar rider filter
  const [sidebarFilter, setSidebarFilter] = useState("");

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const cardRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const fetchLiveSessions = useCallback(async (isManualRefresh = false) => {
    if (!user?.accesstoken) return;

    if (isManualRefresh) {
      setIsRefreshing(true);
    }

    try {
      const sessions = await LiveService.getAllLiveSessions(
        user.accesstoken,
        filters
      );
      setLiveSessions(sessions);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching live sessions:", error);
    } finally {
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  }, [user?.accesstoken, filters]);

  const fetchCountries = useCallback(async () => {
    if (!user?.accesstoken) return;

    try {
      const countryList = await LiveService.getCountries(user.accesstoken);
      setCountries(countryList);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  }, [user?.accesstoken]);

  // Fetch spot requests (only for admins)
  const fetchSpotRequests = useCallback(async () => {
    if (!user?.accesstoken || !user?.isadmin) return;

    setRequestsLoading(true);
    try {
      const requests = await SpotRequestService.getAllPendingRequests(user.accesstoken);
      setSpotRequests(requests);
    } catch (error) {
      console.error("Error fetching spot requests:", error);
    } finally {
      setRequestsLoading(false);
    }
  }, [user?.accesstoken, user?.isadmin]);

  // Initial data fetch
  useEffect(() => {
    fetchLiveSessions();
    fetchCountries();
    if (user?.isadmin) {
      fetchSpotRequests();
    }
  }, [fetchLiveSessions, fetchCountries, fetchSpotRequests, user?.isadmin]);

  // Auto-refresh with configurable interval
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    if (autoRefreshEnabled && viewMode === "riders") {
      setCountdown(refreshInterval);

      // Countdown timer (updates every second)
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : refreshInterval));
      }, 1000);

      // Actual refresh
      refreshIntervalRef.current = setInterval(() => {
        fetchLiveSessions();
        setCountdown(refreshInterval);
      }, refreshInterval * 1000);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [fetchLiveSessions, autoRefreshEnabled, refreshInterval, viewMode]);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply client-side filters (search, device OS, device type)
  useEffect(() => {
    let filtered = liveSessions;

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (session) =>
          (session.user?.name?.toLowerCase() || "").includes(query) ||
          (session.user?.username?.toLowerCase() || "").includes(query)
      );
    }

    // Device OS filter
    if (filters.deviceOs) {
      filtered = filtered.filter((session) => {
        const deviceOs = getDeviceOs(session.device);
        return deviceOs === filters.deviceOs;
      });
    }

    // Device type filter
    if (filters.deviceType) {
      filtered = filtered.filter((session) => {
        const deviceType = getDeviceType(session.device);
        return deviceType === filters.deviceType;
      });
    }

    setFilteredSessions(filtered);
  }, [liveSessions, filters.searchQuery, filters.deviceOs, filters.deviceType]);

  const handleFilterChange = (newFilters: LiveFilters) => {
    setFilters(newFilters);
  };

  // Handler for list clicks - triggers zoom to marker
  const handleSessionSelect = useCallback((sessionId: number) => {
    setSelectionSource("list");
    setSelectedSessionId(prev => prev === sessionId ? null : sessionId);
  }, []);

  // Handler for map marker clicks - scrolls list but no map zoom
  const handleMapSessionSelect = useCallback((sessionId: number) => {
    setSelectionSource("map");
    setSelectedSessionId(prev => {
      const newSelected = prev === sessionId ? null : sessionId;
      // Scroll to card if selecting (not deselecting)
      if (newSelected !== null) {
        setTimeout(() => {
          const cardEl = cardRefsMap.current.get(sessionId);
          if (cardEl) {
            cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }, 100);
      }
      return newSelected;
    });
  }, []);

  const handleSessionHover = useCallback((sessionId: number | null) => {
    setHoveredSessionId(sessionId);
  }, []);

  const handleManualRefresh = () => {
    if (viewMode === "riders") {
      fetchLiveSessions(true);
    } else {
      fetchSpotRequests();
    }
  };

  // Handle spot selection from unified search - fly to spot coordinates
  const handleSpotSelect = useCallback((spot: Spot) => {
    // Use lat/lon (from KwmSpotResponse) or latitude/longitude
    const lat = spot.lat ?? spot.latitude;
    const lng = spot.lon ?? spot.longitude;
    if (lat && lng) {
      setFlyToTarget({ lat, lng, zoom: 14 });
      // Clear any selected session
      setSelectedSessionId(null);
      setSelectionSource(null);
    }
  }, []);

  // Handle user selection from search - same as list selection
  const handleSearchUserSelect = useCallback((sessionId: number) => {
    setSelectionSource("list");
    setSelectedSessionId(sessionId);
  }, []);

  // Handle location selection from search - fly to coordinates
  const handleLocationSelect = useCallback((lat: number, lng: number, _name: string) => {
    setFlyToTarget({ lat, lng, zoom: 12 });
    setSelectedSessionId(null);
    setSelectionSource(null);
  }, []);

  // Handle view mode change - syncs with URL for back/forward and reload
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    // Update URL param for back button and reload support
    setSearchParams((prev) => {
      if (mode === "requests") {
        prev.set("view", "requests");
      } else {
        prev.delete("view"); // Remove param for default (riders) mode
      }
      return prev;
    }, { replace: false }); // Use push for back button support

    // Clear selections when switching modes
    if (mode === "riders") {
      setSelectedRequest(null);
      setHoveredRequest(null);
    } else {
      setSelectedSessionId(null);
      setHoveredSessionId(null);
      // Refresh requests when switching to request mode
      fetchSpotRequests();
    }
  }, [fetchSpotRequests, setSearchParams]);

  // Sync viewMode with URL when browser back/forward is used
  useEffect(() => {
    const viewParam = searchParams.get("view");
    const newMode: ViewMode = viewParam === "requests" ? "requests" : "riders";
    if (newMode !== viewMode) {
      setViewMode(newMode);
      // Clear selections when mode changes via back/forward
      if (newMode === "riders") {
        setSelectedRequest(null);
        setHoveredRequest(null);
      } else {
        setSelectedSessionId(null);
        setHoveredSessionId(null);
        fetchSpotRequests();
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle request selection from list
  const handleRequestSelect = useCallback((request: SpotRequest) => {
    setSelectedRequest(prev =>
      prev?.id === request.id && prev?.type === request.type ? null : request
    );

    // Fly to request location
    if (request.type === "merge") {
      const data = request.data as MergeSpotRequest;
      // Fly to midpoint between source and target (if coordinates are valid)
      if (data.sourceLat != null && data.targetLat != null && data.sourceLon != null && data.targetLon != null) {
        const midLat = (data.sourceLat + data.targetLat) / 2;
        const midLng = (data.sourceLon + data.targetLon) / 2;
        setFlyToTarget({ lat: midLat, lng: midLng, zoom: 12 });
      }
    } else {
      const data = request.data as any;
      if (data.latitude && data.longitude) {
        setFlyToTarget({ lat: data.latitude, lng: data.longitude, zoom: 14 });
      }
    }
  }, []);

  // Handle request hover
  const handleRequestHover = useCallback((request: SpotRequest | null) => {
    setHoveredRequest(request);
  }, []);

  // Handle review request (opens dialog with all options)
  const handleReviewRequest = useCallback((request: SpotRequest) => {
    setConfirmDialog({
      isOpen: true,
      request,
      actionType: "review",
    });
  }, []);

  // Handle user profile click
  const handleUserClick = useCallback((userId: number, username: string) => {
    setUserProfileDialog({
      isOpen: true,
      userId,
      username,
    });
  }, []);

  // Handle swap merge direction
  const handleSwapRequest = useCallback((request: SpotRequest) => {
    if (request.type !== "merge") return;

    const data = request.data as MergeSpotRequest;
    // Get source spot ID (might be in selectedspotid or sourcespotid)
    const sourceSpotId = data.selectedspotid ?? data.sourcespotid;

    // Create swapped version
    const swappedData: MergeSpotRequest = {
      ...data,
      targetspotid: sourceSpotId ?? data.targetspotid,
      selectedspotid: data.targetspotid,
      sourcespotid: data.targetspotid,
      targetspotname: data.sourcespotname,
      sourcespotname: data.targetspotname,
      targetLat: data.sourceLat,
      targetLon: data.sourceLon,
      sourceLat: data.targetLat,
      sourceLon: data.targetLon,
      targetnreviews: data.sourcenreviews,
      sourcenreviews: data.targetnreviews,
      targetnsessions: data.sourcensessions,
      sourcensessions: data.targetnsessions,
    };

    // Update in local state
    setSpotRequests(prev => prev.map(r =>
      r.id === request.id && r.type === request.type
        ? { ...r, data: swappedData }
        : r
    ));

    // Update selected if it's the same request
    if (selectedRequest?.id === request.id && selectedRequest?.type === request.type) {
      setSelectedRequest({ ...request, data: swappedData });
    }
  }, [selectedRequest]);

  // Dialog approve handler
  const handleDialogApprove = useCallback(async () => {
    if (!confirmDialog.request || !user?.accesstoken) return;

    setIsActionLoading(true);
    try {
      const request = confirmDialog.request;
      let result: { success: boolean; message?: string };

      if (request.type === "merge") {
        const data = request.data as MergeSpotRequest;
        // Get source spot ID (might be in selectedspotid or sourcespotid)
        const sourceSpotId = data.selectedspotid ?? data.sourcespotid;
        if (sourceSpotId == null) {
          throw new Error("Source spot ID not found");
        }
        result = await SpotRequestService.approveMergeRequest(
          user.accesstoken,
          data.targetspotid,
          [sourceSpotId],
          data.id
        );
      } else {
        // Handle add, rename, or edit request
        const isChangeRequest = request.type === "rename" || request.type === "edit";
        const addData = request.data as SpotAddRequest;

        if (isChangeRequest) {
          // Rename/edit requests use the dedicated acceptrename endpoint
          // (backend now applies both name and location changes on approval)
          result = await SpotRequestService.approveAddRequest(user.accesstoken, request.id, true);
        } else {
          // ADD requests create the spot via POST /spots with srid parameter
          result = await SpotRequestService.approveAddRequest(
            user.accesstoken,
            request.id,
            false,
            {
              name: addData.spotname,
              latitude: addData.latitude,
              longitude: addData.longitude,
            }
          );
        }
      }

      // Only update local state if the API call succeeded
      if (!result.success) {
        console.error("Failed to approve request:", result.message);
        // Keep dialog open to show error - user can retry or cancel
        return;
      }

      // Remove request from local state
      setSpotRequests(prev => prev.filter(r =>
        !(r.id === request.id && r.type === request.type)
      ));

      // Clear selection if it was the processed request
      if (selectedRequest?.id === request.id && selectedRequest?.type === request.type) {
        setSelectedRequest(null);
      }

      // Close dialog
      setConfirmDialog({ isOpen: false, request: null, actionType: "approve" });
    } catch (error) {
      console.error("Error approving request:", error);
    } finally {
      setIsActionLoading(false);
    }
  }, [confirmDialog.request, user?.accesstoken, selectedRequest]);

  // Dialog decline handler
  const handleDialogDecline = useCallback(async (reason?: string) => {
    if (!confirmDialog.request || !user?.accesstoken) return;

    setIsActionLoading(true);
    try {
      const request = confirmDialog.request;

      if (request.type === "merge") {
        await SpotRequestService.declineMergeRequest(user.accesstoken, request.id, reason);
      } else {
        await SpotRequestService.declineAddRequest(user.accesstoken, request.id, reason);
      }

      // Remove request from local state
      setSpotRequests(prev => prev.filter(r =>
        !(r.id === request.id && r.type === request.type)
      ));

      // Clear selection if it was the processed request
      if (selectedRequest?.id === request.id && selectedRequest?.type === request.type) {
        setSelectedRequest(null);
      }

      // Close dialog
      setConfirmDialog({ isOpen: false, request: null, actionType: "approve" });
    } catch (error) {
      console.error("Error processing request:", error);
    } finally {
      setIsActionLoading(false);
    }
  }, [confirmDialog, user?.accesstoken, selectedRequest]);

  // Filter sessions for sidebar display
  const sidebarSessions = sidebarFilter.trim()
    ? filteredSessions.filter(
        (s) =>
          s.user.name.toLowerCase().includes(sidebarFilter.toLowerCase()) ||
          s.user.username?.toLowerCase().includes(sidebarFilter.toLowerCase())
      )
    : filteredSessions;

  const formatLastRefresh = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Compact Header Row - Title, Search, Filters, Refresh */}
      <div className="flex-shrink-0 mb-2 md:mb-3">
        <div className="flex items-center gap-2 md:gap-3">
          {/* Title & Count */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <h1 className="text-lg md:text-xl font-bold text-gray-900">Live</h1>
          </div>

          {/* View Mode Switcher (Admin only) */}
          {user?.isadmin && (
            <ViewModeSwitcher
              mode={viewMode}
              onModeChange={handleViewModeChange}
              ridersCount={filteredSessions.length}
              requestsCount={spotRequests.length}
            />
          )}

          {/* Non-admin badge */}
          {!user?.isadmin && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium">
              {filteredSessions.length}
            </span>
          )}

          {/* Filters Component (Search + Filter button + Advanced panel) - hidden on mobile/tablet */}
          <div className="hidden lg:flex flex-1">
            <LiveFiltersComponent
              filters={filters}
              onFilterChange={handleFilterChange}
              countries={countries}
              sessions={filteredSessions}
              onSpotSelect={handleSpotSelect}
              onLocationSelect={handleLocationSelect}
              onUserSelect={handleSearchUserSelect}
              hideAdvancedFilters={viewMode === "requests"}
              spotRequests={viewMode === "requests" ? spotRequests : undefined}
              onRequestSelect={viewMode === "requests" ? handleRequestSelect : undefined}
            />
          </div>
          <div className="flex-1 lg:hidden"></div>

          {/* Refresh Controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Auto-refresh countdown (only in riders mode) */}
            {autoRefreshEnabled && viewMode === "riders" && (
              <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 rounded text-xs text-gray-600">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span>{countdown}s</span>
              </div>
            )}

            {/* Refresh button */}
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing || requestsLoading}
              className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              title={viewMode === "riders" ? `Refresh now | Last: ${formatLastRefresh(lastRefresh)}` : "Refresh requests"}
            >
              <ArrowPathIcon className={`w-4 h-4 ${(isRefreshing || requestsLoading) ? "animate-spin" : ""}`} />
            </button>

            {/* Settings dropdown (only in riders mode) */}
            {viewMode === "riders" && (
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`p-1.5 rounded transition-colors ${
                    showSettings ? "bg-gray-200 text-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  title="Refresh settings"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                </button>

                {showSettings && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Auto-refresh Settings</h3>

                    {/* Toggle */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-700">Auto-refresh</span>
                      <button
                        onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          autoRefreshEnabled ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoRefreshEnabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Interval selector */}
                    <div className={autoRefreshEnabled ? "" : "opacity-50 pointer-events-none"}>
                      <label className="text-sm text-gray-700 mb-2 block">Refresh interval</label>
                      <div className="flex flex-wrap gap-1">
                        {[5, 10, 15, 30, 60, 120, 300].map((seconds) => (
                          <button
                            key={seconds}
                            onClick={() => setRefreshInterval(seconds)}
                            className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
                              refreshInterval === seconds
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {seconds < 60 ? `${seconds}s` : `${seconds / 60}m`}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-4"></div>

                    {/* Spot Settings */}
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-red-500" />
                      Spot Markers
                    </h3>

                    {/* Show spots toggle */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-700">Show spots on map</span>
                      <button
                        onClick={() => setSpotSettings({ ...spotSettings, showSpots: !spotSettings.showSpots })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          spotSettings.showSpots ? "bg-red-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            spotSettings.showSpots ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Min zoom level selector */}
                    <div className={spotSettings.showSpots ? "" : "opacity-50 pointer-events-none"}>
                      <label className="text-sm text-gray-700 mb-2 block">
                        Minimum zoom level <span className="text-gray-400">(current: {spotSettings.minZoomLevel})</span>
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {[8, 9, 10, 11, 12, 13].map((level) => (
                          <button
                            key={level}
                            onClick={() => setSpotSettings({ ...spotSettings, minZoomLevel: level })}
                            className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
                              spotSettings.minZoomLevel === level
                                ? "bg-red-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Lower = more zoomed out</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex lg:gap-4 flex-1 min-h-0">
        {/* Left Sidebar - Conditionally render based on view mode */}
        {viewMode === "riders" ? (
          /* Riders Mode - Session List (hidden on mobile) */
          <div className="hidden lg:flex w-96 flex-shrink-0 bg-gray-900 rounded-lg overflow-hidden flex-col">
            <div className="p-3 bg-gray-800 border-b border-gray-700 space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">
                  Live Riders ({sidebarSessions.length})
                </h2>
                {sidebarFilter && sidebarSessions.length !== filteredSessions.length && (
                  <span className="text-xs text-gray-400">
                    of {filteredSessions.length}
                  </span>
                )}
              </div>
              {/* Simple rider filter */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={sidebarFilter}
                  onChange={(e) => setSidebarFilter(e.target.value)}
                  placeholder="Filter riders by name..."
                  className="w-full pl-8 pr-8 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {sidebarFilter && (
                  <button
                    onClick={() => setSidebarFilter("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <svg className="w-4 h-4 text-gray-400 hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {initialLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : sidebarSessions.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  {sidebarFilter ? "No riders match filter" : "No live sessions found"}
                </div>
              ) : (
                sidebarSessions.map((session) => (
                  <LiveSessionCard
                    key={session.id}
                    ref={(el) => {
                      if (el) {
                        cardRefsMap.current.set(session.id, el);
                      } else {
                        cardRefsMap.current.delete(session.id);
                      }
                    }}
                    session={session}
                    isSelected={selectedSessionId === session.id}
                    isHovered={hoveredSessionId === session.id}
                    onSelect={() => handleSessionSelect(session.id)}
                    onHover={() => handleSessionHover(session.id)}
                    onHoverEnd={() => handleSessionHover(null)}
                    onShowDetails={() => setDetailsSession(session)}
                  />
                ))
              )}
            </div>
          </div>
        ) : (
          /* Requests Mode - Request List Panel or Add Spot Panel */
          <div className="hidden lg:flex w-96 flex-shrink-0">
            {isAddingSpot ? (
              <AddSpotPanel
                location={addSpotLocation}
                onCancel={() => {
                  setIsAddingSpot(false);
                  setAddSpotLocation(null);
                  setAddSpotNearbySpots([]);
                }}
                onCreated={() => {
                  setIsAddingSpot(false);
                  setAddSpotLocation(null);
                  setAddSpotNearbySpots([]);
                  // Refresh requests list
                  if (user?.accesstoken) {
                    SpotRequestService.getAllPendingRequests(user.accesstoken).then(setSpotRequests);
                  }
                }}
                onNearbySpots={setAddSpotNearbySpots}
              />
            ) : (
              <RequestListPanel
                requests={spotRequests}
                selectedRequestId={selectedRequest?.id ?? null}
                selectedRequestType={selectedRequest?.type ?? null}
                hoveredRequestId={hoveredRequest?.id ?? null}
                hoveredRequestType={hoveredRequest?.type ?? null}
                onRequestSelect={handleRequestSelect}
                onRequestHover={handleRequestHover}
                onReview={handleReviewRequest}
                onUserClick={handleUserClick}
                filters={requestFilters}
                onFiltersChange={setRequestFilters}
                onFilteredRequestsChange={setFilteredRequests}
                isLoading={requestsLoading}
                onAddSpot={() => setIsAddingSpot(true)}
              />
            )}
          </div>
        )}

        {/* Right Side - Map */}
        <div className="flex-1 rounded-lg overflow-hidden relative z-0">
          <LiveMap
            sessions={filteredSessions}
            selectedSessionId={selectedSessionId}
            selectionSource={selectionSource}
            hoveredSessionId={hoveredSessionId}
            onSessionSelect={handleMapSessionSelect}
            onSessionHover={handleSessionHover}
            onShowDetails={setDetailsSession}
            spotSettings={spotSettings}
            onSpotSettingsChange={setSpotSettings}
            flyToTarget={flyToTarget}
            onFlyToComplete={() => setFlyToTarget(null)}
            // Request mode props
            viewMode={viewMode}
            spotRequests={filteredRequests}
            selectedRequest={selectedRequest}
            hoveredRequest={hoveredRequest}
            onRequestSelect={handleRequestSelect}
            onReviewRequest={handleReviewRequest}
            onSwapRequest={handleSwapRequest}
            // Add spot mode props
            addSpotMode={isAddingSpot}
            addSpotLocation={addSpotLocation}
            addSpotNearbySpots={addSpotNearbySpots}
            onMapClickForSpot={(lat, lng) => setAddSpotLocation({ lat, lng })}
            onAddSpotDrag={(lat, lng) => setAddSpotLocation({ lat, lng })}
          />
        </div>
      </div>

      {/* Jump Details Modal */}
      {detailsSession && (
        <JumpDetailsModal
          session={detailsSession}
          onClose={() => setDetailsSession(null)}
        />
      )}

      {/* Confirm Action Dialog */}
      {confirmDialog.request && (
        <ConfirmActionDialog
          request={confirmDialog.request}
          actionType={confirmDialog.actionType}
          isOpen={confirmDialog.isOpen}
          isLoading={isActionLoading}
          onApprove={handleDialogApprove}
          onDecline={handleDialogDecline}
          onCancel={() => setConfirmDialog({ isOpen: false, request: null, actionType: "approve" })}
          onUserClick={handleUserClick}
        />
      )}

      {/* User Profile Dialog */}
      <UserProfileDialog
        userId={userProfileDialog.userId}
        username={userProfileDialog.username}
        isOpen={userProfileDialog.isOpen}
        accessToken={user?.accesstoken || ""}
        onClose={() => setUserProfileDialog({ isOpen: false, userId: 0, username: "" })}
      />
    </div>
  );
};

export default LivePage;
